import { Box, Button, Chip, IconButton, InputBase, Popover, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QUANTITY_UOM_MAX_LENGTH } from '../../../lib/packingListFields';
import { addQuantity, ensureQuantities, formatQuantity, removeQuantity, secondaryQuantities, setPrimaryQuantity, updateQuantity } from '../../../lib/packingListQuantity';

const BORDER = '1px solid #e0e0e0';
const ROW_HEIGHT = 30;
/** Line Items 그리드와 같은 모양(머리 회색, 11px, 칸 테두리)으로 맞춘 표. */
const tableSx = {
    border: BORDER,
    tableLayout: 'fixed',
    '& .MuiTableCell-root': { p: 0, height: ROW_HEIGHT, fontSize: 11, borderBottom: BORDER, borderRight: BORDER },
    '& .MuiTableCell-root:last-of-type': { borderRight: 'none' },
    '& .MuiTableCell-head': { bgcolor: '#f1f1f1', color: 'text.secondary', fontWeight: 500, textAlign: 'center', height: 28 },
    '& .MuiInputBase-root': { width: '100%', height: ROW_HEIGHT - 1, fontSize: 11 },
    '& .MuiInputBase-input': { px: 1, py: 0 },
    '& .MuiTableCell-body:focus-within': { outline: '1px solid #00647e', outlineOffset: -1 },
};

/**
 * 한 라인의 수량 목록 편집 창. 같은 품목을 PCS·CTN처럼 여러 단위로 적고, 그중 하나를 대표 수량으로 고른다.
 * 대표 수량은 그리드의 Qty·UOM 칸과 같은 값이며 합계 대조와 컨테이너 배분의 기준이 된다.
 * 표 안의 칸이 곧 입력칸이라 누르면 바로 고치고, Tab으로 Qty → UOM → 다음 줄로 옮겨 간다.
 */
function QuantityEditor({ anchorEl, line, onClose, onChange }) {
    const { quantities } = ensureQuantities(line);
    return (
        <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { p: 1.5, width: 330 } } }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#01354d' }}>Quantities · Line {line.line_no ?? ''}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>The primary quantity is used for totals and container allocation.</Typography>
            <Table size="small" sx={tableSx}>
                <colgroup>
                    <col style={{ width: 66 }} />
                    <col />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 32 }} />
                </colgroup>
                <TableHead>
                    <TableRow>
                        <TableCell>Primary</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>UOM</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {quantities.map((q) => (
                        <QuantityRow key={q.key} quantity={q} removable={quantities.length > 1} onPrimary={() => onChange((l) => setPrimaryQuantity(l, q.key))} onPatch={(patch) => onChange((l) => updateQuantity(l, q.key, patch))} onRemove={() => onChange((l) => removeQuantity(l, q.key))} />
                    ))}
                </TableBody>
            </Table>
            <Button size="small" startIcon={<AddIcon />} onClick={() => onChange((l) => addQuantity(l))} sx={{ fontSize: 11, mt: 1 }}>Add quantity</Button>
        </Popover>
    );
}

/**
 * 수량 한 줄 편집. 단위는 PL에 적힌 원문을 그대로 받는 글자 칸이다(마스터 목록을 고르지 않는다).
 * 친 그대로를 단위와 원문(uom_raw)에 함께 두고, 칸을 벗어날 때 앞뒤 공백만 걷는다. 0 이하·빈 수량은 칸을 붉게 칠한다(확정은 검증이 막는다).
 */
function QuantityRow({ quantity, removable, onPrimary, onPatch, onRemove }) {
    const invalidQty = quantity.qty === null || Number(quantity.qty) <= 0;
    const writeUom = (value) => onPatch({ uom: value, uom_raw: value || null });
    const finishUom = () => {
        const written = String(quantity.uom ?? '').trim();
        if (written !== quantity.uom) writeUom(written);
    };
    return (
        <TableRow>
            <TableCell align="center">
                {quantity.is_primary ? (
                    <Chip size="small" label="Primary" sx={{ height: 18, fontSize: 10, bgcolor: '#00647e', color: 'white' }} />
                ) : (
                    <Chip size="small" variant="outlined" label="Set" clickable onClick={onPrimary} aria-label="Set as primary quantity" sx={{ height: 18, fontSize: 10, color: 'text.secondary' }} />
                )}
            </TableCell>
            <TableCell sx={{ bgcolor: invalidQty ? 'rgba(217,41,54,0.12)' : undefined }}>
                <InputBase type="number" value={quantity.qty ?? ''} onChange={(e) => onPatch({ qty: e.target.value })} inputProps={{ 'aria-label': 'Quantity', style: { textAlign: 'right' } }} />
            </TableCell>
            <TableCell>
                <InputBase value={quantity.uom ?? ''} onChange={(e) => writeUom(e.target.value)} onBlur={finishUom} placeholder="As written on the PL" inputProps={{ 'aria-label': 'UOM', maxLength: QUANTITY_UOM_MAX_LENGTH }} />
            </TableCell>
            <TableCell align="center">
                <Tooltip title={removable ? 'Remove this quantity' : 'A line needs at least one quantity'}>
                    <span>
                        <IconButton size="small" onClick={onRemove} disabled={!removable}>
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}

/** 보조 수량 한 줄. 단위는 PL 원문이라 마스터와 대조하지 않고 적힌 그대로 보여준다. */
function QuantityLine({ quantity, suffix = '' }) {
    return (
        <Typography noWrap sx={{ fontSize: 11, lineHeight: 1.2 }}>
            {formatQuantity(quantity)}
            {suffix && <span style={{ color: '#7a7a7a' }}>{suffix}</span>}
        </Typography>
    );
}

/**
 * 그리드 셀. 대표가 아닌 수량을 한 줄에 하나씩 최대 두 줄로 보여준다. 셋 이상이면 둘째 줄에 "+N"을 붙이고 툴팁에 전부 보여준다.
 * 편집은 셀을 누르거나 Enter로 여는 수량 편집 창에서 한다(그리드가 연다).
 */
export function QuantitiesCell({ line }) {
    const others = secondaryQuantities(ensureQuantities(line).quantities);
    if (others.length === 0) return <Typography sx={{ fontSize: 11, color: 'text.disabled', width: '100%', textAlign: 'center' }}>—</Typography>;
    const shown = others.slice(0, 2);
    const hidden = others.length - shown.length;
    const content = (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%', px: 1, overflow: 'hidden', cursor: 'pointer' }}>
            {shown.map((q, i) => <QuantityLine key={q.key} quantity={q} suffix={i === shown.length - 1 && hidden > 0 ? ` +${hidden}` : ''} />)}
        </Box>
    );
    if (hidden === 0) return content;
    return <Tooltip title={others.map(formatQuantity).join(' · ')} arrow>{content}</Tooltip>;
}

export default QuantityEditor;
